<template>
  <div class="rai-dashboard">
    <!-- Map Component -->
    <div id="rai-map" class="rai-map">
      <div v-if="loading" class="map-placeholder">
        <v-icon size="64" color="grey">mdi-loading mdi-spin</v-icon>
        <h3 class="mt-4">Loading World Map...</h3>
        <p>Fetching RAI data from server</p>
      </div>
      
      <div v-else-if="mapError" class="map-placeholder">
        <v-icon size="64" color="error">mdi-alert-circle</v-icon>
        <h3 class="mt-4">Map Loading Error</h3>
        <p>{{ mapError }}</p>
        <v-btn @click="loadMapData" color="primary" class="mt-2">
          Retry
        </v-btn>
      </div>
      
      <!-- Leaflet Map -->
      <l-map
        v-else
        ref="leafletMap"
        :zoom="zoom"
        :center="center"
        :options="mapOptions"
        class="leaflet-map"
      >
        <!-- Grey basemap tile layer -->
        <l-tile-layer
          :url="tileUrl"
          :attribution="attribution"
          :options="tileOptions"
        />
        
        <!-- Country GeoJSON layer -->
        <l-geo-json
          v-if="geojsonData"
          :geojson="geojsonData"
          :options="geojsonOptions"
        />
      </l-map>
      
      <!-- Legend Card -->
      <v-card 
        v-if="!loading && !mapError"
        class="legend-card"
        elevation="4"
      >
        <v-card-title class="legend-title py-2">
          <v-icon small class="mr-2">mdi-map-legend</v-icon>
          Rural Access Index (RAI)
        </v-card-title>
        <v-card-text class="legend-content py-0">
          <div class="legend-items">
            <div class="legend-item">
              <div class="legend-color" style="background-color: #395A7F;"></div>
              <span class="legend-text">100% (Dark Blue)</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background-color: #5CB2F9;"></div>
              <span class="legend-text">68% - 99% (Medium Blue)</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background-color: #A3D4F7;"></div>
              <span class="legend-text">33% - 67% (Light Blue)</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background-color: #E6F3FB;"></div>
              <span class="legend-text">0% - 32% (Very Light Blue)</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background-color: #F8F9FA;"></div>
              <span class="legend-text">No Data (Very Light Gray)</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
      
      <!-- Tooltip for hover information -->
      <div
        v-if="hoveredCountry"
        class="map-tooltip"
        :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
      >
        {{ hoveredCountry.name }}: {{ hoveredCountry.rai !== null && hoveredCountry.rai !== undefined ? hoveredCountry.rai + '%' : 'No Data' }}
      </div>
    </div>

    <!-- Country Details Modal -->
    <v-dialog
      v-model="showCountryDialog"
      max-width="1200px"
      :overlay-opacity="0.2"
      :overlay="false"
    >
      <v-card>
        <v-card-title class="country-modal-header">
          <h2>{{ selectedCountry.name }}</h2>
          <v-spacer></v-spacer>
          <v-btn
            icon
            class="country-modal-close"
            aria-label="Close country details"
            title="Close"
            @click="closeCountryDialog"
          >
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-4">
          <!-- Country RAI Info -->
          <div class="country-info mb-4">
            <h3 class="mb-2">Rural Access Index</h3>
            <div class="rai-value">
              {{ selectedCountry.rai !== null && selectedCountry.rai !== undefined ? selectedCountry.rai + '%' : 'No Data Available' }}
            </div>
          </div>

          <!-- Charts Container -->
          <div class="charts-container">
            <!-- Population Chart -->
            <div class="chart-section">
              <h4 class="chart-title">Population: Total vs Rural</h4>
              <div class="chart-placeholder">
                <div v-if="!countryDetailData" class="chart-loading">
                  <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                  <p class="mt-2">Loading population data...</p>
                </div>
                <canvas v-else ref="populationChart" width="400" height="250"></canvas>
              </div>
            </div>

            <!-- GDP Chart -->
            <div class="chart-section">
              <h4 class="chart-title">GDP Trend</h4>
              <div class="chart-placeholder">
                <div v-if="!countryDetailData" class="chart-loading">
                  <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                  <p class="mt-2">Loading GDP data...</p>
                </div>
                <canvas v-else ref="gdpChart" width="400" height="250"></canvas>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { LMap, LTileLayer, LGeoJson } from 'vue2-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
)

// Fix for default markers in Leaflet
delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

export default {
  name: 'RAI',
  components: {
    LMap,
    LTileLayer,
    LGeoJson
  },
  data() {
    return {
      loading: true,
      mapError: null,
      geojsonData: null,
      raiData: {},
      hoveredCountry: null,
      tooltipX: 0,
      tooltipY: 0,
      // Country details modal
      showCountryDialog: false,
      selectedCountry: {
        name: '',
        code: '',
        rai: null
      },
      // Chart instances
      populationChartInstance: null,
      gdpChartInstance: null,
      // Country detailed data
      countryDetailData: null,
      // Leaflet map settings
      zoom: 2,
      center: [35, 0], // Shifted down by moving latitude from 0 to -20
      // Public no-auth grey basemap for production-safe hosting
      tileUrl: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      tileOptions: {
        maxZoom: 18,
        subdomains: 'abcd'
      },
      mapOptions: {
        zoomControl: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        touchZoom: true,
        boxZoom: true,
        keyboard: true,
        dragging: true,
        minZoom: 1,
        maxZoom: 10
      },
      // GeoJSON layer options
      geojsonOptions: {
        style: this.getFeatureStyle,
        onEachFeature: this.onEachFeature
      }
    }
  },
  
  mounted() {
    this.loadMapData()
  },
  
  methods: {
    async loadMapData() {
      try {
        this.loading = true
        this.mapError = null
        
        // Fetch both GeoJSON and RAI data in parallel
        const [geoResponse, raiResponse] = await Promise.all([
          fetch(this.$domain + '/data/world.topo.geojson'),
          fetch(this.$domain + '/data/rural_rate')
        ])
        
        if (!geoResponse.ok) {
          throw new Error(`GeoJSON fetch error! status: ${geoResponse.status}`)
        }
        if (!raiResponse.ok) {
          throw new Error(`RAI data fetch error! status: ${raiResponse.status}`)
        }
        
        this.geojsonData = await geoResponse.json()
        const raiArray = await raiResponse.json()
        
        // Convert RAI array to lookup object by country code
        this.raiData = {}
        raiArray.forEach(item => {
          if (item.code) {
            this.raiData[item.code] = {
              rai: item.pct_rural_served_RAI ? Math.round(item.pct_rural_served_RAI * 100) : null,
              name: item.name
            }
          }
        })
        
        console.log('RAI Data loaded:', this.raiData)
        console.log('GeoJSON Data loaded:', this.geojsonData)
        
        this.loading = false
      } catch (error) {
        console.error('Error loading map data:', error)
        this.mapError = `Failed to load map data: ${error.message}`
        this.loading = false
      }
    },    
  
    getFeatureStyle(feature) {
      const countryCode = feature.properties.iso3
      const raiInfo = this.raiData[countryCode] || {}
      const rai = raiInfo.rai || null
      
      return {
        fillColor: this.getCountryColor(rai),
        weight: 1,
        opacity: 1,
        color: '#666',
        fillOpacity: 0.8
      }
    },
    
    getCountryColor(rai) {
      if (rai === null || rai === undefined) return '#F8F9FA'  // No Data - very light gray
      if (rai == 100) return '#395A7F'      // 100% - dark blue
      if (rai >= 2/3*100) return '#5CB2F9'  // 68% - medium blue
      if (rai >= 1/3*100) return '#A3D4F7'  // 33% - light blue
      if (rai >= 0) return '#E6F3FB'        // 0% - very light blue
      return '#F8F9FA'                      // No Data - very light gray
    },
    
    onEachFeature(feature, layer) {
      const countryCode = feature.properties.iso3
      const countryName = feature.properties.name
      const raiInfo = this.raiData[countryCode] || {}
      const rai = raiInfo.rai || null
      const displayName = raiInfo.name || countryName
      
      // Add click event
      layer.on('click', () => {
        this.selectCountryFromMap({
          name: displayName,
          code: countryCode,
          rai: rai
        })
      })
      
      // Add mouseover event
      layer.on('mouseover', (e) => {
        // Highlight the layer
        layer.setStyle({
          weight: 3,
          color: '#000',
          fillOpacity: 0.9
        })
        
        // Update tooltip
        this.hoveredCountry = {
          name: displayName,
          rai: rai
        }
        
        // Get mouse position for tooltip
        const containerPoint = e.containerPoint
        this.tooltipX = containerPoint.x
        this.tooltipY = containerPoint.y
      })
      
      // Add mouseout event
      layer.on('mouseout', () => {
        // Reset style
        layer.setStyle(this.getFeatureStyle(feature))
        this.hoveredCountry = null
      })
      
      // Add mousemove event for tooltip positioning
      layer.on('mousemove', (e) => {
        if (this.hoveredCountry) {
          const containerPoint = e.containerPoint
          this.tooltipX = containerPoint.x
          this.tooltipY = containerPoint.y
        }
      })
    },
    
    selectCountryFromMap(feature) {
      console.log('Selected country:', feature.name, 'RAI:', feature.rai ? feature.rai + '%' : 'No Data')
      
      // Set selected country data
      this.selectedCountry = {
        name: feature.name,
        code: feature.code,
        rai: feature.rai
      }
      
      // Show the modal
      this.showCountryDialog = true
      
      // Fetch country details and create charts
      this.fetchCountryDetails(feature.code)
    },

    async fetchCountryDetails(countryCode) {
      try {
        // Show loading state in charts
        this.countryDetailData = null
        
        // Fetch country index data
        const response = await fetch(`${this.$domain}/rai/country-index?code=${countryCode}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        
        if (!response.ok) {
          throw new Error(`API error! status: ${response.status}`)
        }
        
        const data = await response.json()
        this.countryDetailData = data
        
        console.log('Country detail data:', data)
        
        // Wait for modal to be rendered, then create charts with real data
        this.$nextTick(() => {
          this.createCharts()
        })
        
      } catch (error) {
        console.error('Error fetching country details:', error)
        // Create charts with sample data as fallback
        this.$nextTick(() => {
          this.createCharts()
        })
      }
    },

    closeCountryDialog() {
      this.showCountryDialog = false
      this.destroyCharts()
    },

    resetMap() {
      if (this.$refs.leafletMap && this.$refs.leafletMap.mapObject) {
        this.$refs.leafletMap.mapObject.setView([35, 0], 2)
      }
    },

    createCharts() {
      this.createPopulationChart()
      this.createGDPChart()
    },

    destroyCharts() {
      if (this.populationChartInstance) {
        this.populationChartInstance.destroy()
        this.populationChartInstance = null
      }
      if (this.gdpChartInstance) {
        this.gdpChartInstance.destroy()
        this.gdpChartInstance = null
      }
    },

    createPopulationChart() {
      const canvas = this.$refs.populationChart
      if (!canvas) return

      // Destroy existing chart if it exists
      if (this.populationChartInstance) {
        this.populationChartInstance.destroy()
      }

      const ctx = canvas.getContext('2d')
      
      // Use real data if available, otherwise use sample data
      let years, ruralPop, totalPop
      
      if (this.countryDetailData && this.countryDetailData.data) {
        // Extract years and population data from API response
        const dataArray = this.countryDetailData.data
        years = dataArray.map(item => item.year.toString())
        ruralPop = dataArray.map(item => {
          // Calculate rural population from total population and rural percentage
          const totalPop = item.population || 0
          const ruralPercent = item.rural_population_percent || 0
          return Math.round(totalPop * ruralPercent / 100)
        })
        totalPop = dataArray.map(item => item.population || 0)
      } else {
        // Fallback to sample data or display no data available
        years = []
        ruralPop = []
        totalPop = []
        // Optionally, you can display a message on the chart if no data is available
        if (canvas) {
          ctx.clearRect(0, 0, canvas.width, canvas.height)
          ctx.font = '16px Arial'
          ctx.fillStyle = '#666'
          ctx.textAlign = 'center'
          ctx.fillText('No data available', canvas.width / 2, canvas.height / 2)
        }
        return
      }

      // Create Chart.js chart
      this.populationChartInstance = new ChartJS(ctx, {
        type: 'line',
        data: {
          labels: years,
          datasets: [
            {
              label: 'Total',
              data: totalPop,
              borderColor: '#5CB2F9',
              backgroundColor: 'rgba(92, 178, 249, 0.1)',
              borderWidth: 3,
              pointBackgroundColor: '#5CB2F9',
              pointBorderColor: '#5CB2F9',
              pointRadius: 2,
              pointHoverRadius: 4,
              fill: true,
              tension: 0.4
            },
            {
              label: 'Rural',
              data: ruralPop,
              borderColor: 'rgb(255, 99, 132)',
              backgroundColor: 'rgba(255, 99, 132, 0.1)',
              borderWidth: 3,
              pointBackgroundColor: 'rgb(255, 99, 132)',
              pointBorderColor: 'rgb(255, 99, 132)',
              pointRadius: 2,
              pointHoverRadius: 4,
              fill: true,
              tension: 0.4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                usePointStyle: true,
                padding: 20
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              callbacks: {
                label: function(context) {
                  return context.dataset.label + ': ' + context.parsed.y.toLocaleString()
                }
              }
            }
          },
          scales: {
            x: {
              display: true,
              grid: {
                display: false
              }
            },
            y: {
              display: true,
              grid: {
                color: '#f0f0f0'
              },
              ticks: {
                maxTicksLimit: 4,
                callback: function(value) {
                  if (value >= 1000000) {
                    return (value / 1000000).toFixed(0) + 'M'
                  }
                  return value.toLocaleString()
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart'
          }
        }
      })
    },

    createGDPChart() {
      const canvas = this.$refs.gdpChart
      if (!canvas) return

      // Destroy existing chart if it exists
      if (this.gdpChartInstance) {
        this.gdpChartInstance.destroy()
      }

      const ctx = canvas.getContext('2d')
      
      // Use real data if available, otherwise use sample data
      let years, gdpData
      
      if (this.countryDetailData && this.countryDetailData.data) {
        // Extract years and GDP data from API response
        const dataArray = this.countryDetailData.data
        years = dataArray.map(item => item.year.toString())
        gdpData = dataArray.map(item => item.gdp || 0)
      } else {
        // Fallback to sample data
        years = ['2010', '2012', '2014', '2016', '2018', '2020', '2022']
        gdpData = [500000000, 600000000, 800000000, 1200000000, 1500000000, 1800000000, 2000000000]
      }

      // Create Chart.js chart
      this.gdpChartInstance = new ChartJS(ctx, {
        type: 'line',
        data: {
          labels: years,
          datasets: [
            {
              label: 'GDP (USD)',
              data: gdpData,
              borderColor: '#5CB2F9',
              backgroundColor: 'rgba(92, 178, 249, 0.2)',
              borderWidth: 3,
              pointBackgroundColor: '#5CB2F9',
              pointBorderColor: '#5CB2F9',
              pointRadius: 2,
              pointHoverRadius: 4,
              fill: true,
              tension: 0.4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                usePointStyle: true,
                padding: 20
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              callbacks: {
                label: function(context) {
                  const value = context.parsed.y
                  return 'GDP: $' + (value / 1000000000).toFixed(2) + 'B'
                }
              }
            }
          },
          scales: {
            x: {
              display: true,
              grid: {
                display: false
              }
            },
            y: {
              display: true,
              grid: {
                color: '#f0f0f0'
              },
              ticks: {
                maxTicksLimit: 4,
                callback: function(value) {
                  if (value >= 1000000000) {
                    return '$' + (value / 1000000000).toFixed(0) + 'B'
                  }
                  return '$' + (value / 1000000).toFixed(0) + 'M'
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          },
          animation: {
            duration: 2000,
            easing: 'easeInOutQuart'
          }
        }
      })
    }
  }
}
</script>


<style scoped>
.rai-dashboard {
  height: calc(100vh - 64px);
  overflow: hidden;
}

.rai-map {
  height: 100%;
  width: 100%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.map-placeholder {
  text-align: center;
  color: #666;
}

.leaflet-map {
  width: 100%;
  height: 100%;
  z-index: 1;
}

/* Tooltip styles */
.map-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  pointer-events: none;
  z-index: 1000;
  transform: translate(-50%, -100%);
  margin-top: -10px;
}

.map-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.8) transparent transparent transparent;
}

/* Legend Card Styles */
.legend-card {
  position: absolute;
  bottom: 20px;
  right: 20px;
  max-width: 250px;
  max-height: calc(100% - 40px);
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 1000;
  overflow: hidden;
}

.legend-title {
  background: #002244 !important;
  color: white !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}

.legend-content {
  padding: 8px 12px !important;
  max-height: calc(100vh - 160px);
  overflow-y: auto;
}

.legend-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 20px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.legend-text {
  font-size: 12px;
  color: #333;
  font-weight: 500;
}

/* Country Modal Styles */
.country-modal-header {
  background: #002244 !important;
  color: white !important;
  align-items: center;
}

.country-modal-header h2 {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.country-modal-close {
  flex: 0 0 auto;
  margin-left: 12px;
}

.country-modal-close:hover {
  background: rgba(255, 255, 255, 0.14);
}

.country-info {
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.country-info h3 {
  color: #002244;
  margin-bottom: 10px;
}

.rai-value {
  font-size: 36px;
  font-weight: bold;
  color: #5CB2F9;
}

.charts-container {
  display: flex;
  gap: 20px;
  margin-top: 20px;
}

.chart-section {
  flex: 1;
  min-width: 0;
}

.chart-title {
  color: #002244;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  text-align: center;
}

.chart-placeholder {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-placeholder canvas {
  max-width: 100%;
  max-height: 100%;
}

.chart-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
}

.chart-loading p {
  font-size: 14px;
  margin: 0;
}

/* Responsive design for charts */
@media (max-width: 768px) {
  .charts-container {
    flex-direction: column;
  }
  
  .chart-placeholder {
    height: 250px;
  }
}
</style>

<style>
/* Fix the specific Vuetify overlay scrim class */
.v-overlay__scrim {
  background-color: rgba(33, 33, 33, 0.1) !important;
}

/* Target the dialog overlay specifically */
.v-dialog__content .v-overlay__scrim {
  background-color: rgba(33, 33, 33, 0.15) !important;
}
</style>
